import { useCallback, useEffect, useState } from 'react';
import { Copy, Database, Edit, Eye, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { errorMessage } from '../../components/backup/BackupFields';
import { usePermissions } from '../../hooks/usePermissions';

const BackupToolsPage = () => {
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('config', 'backup_tools');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(null);
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/config/backup-tools');
      setItems(response.data.items);
      setError('');
    } catch (err) {
      setError(errorMessage(err, 'No se pudieron cargar las herramientas de backup'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (canRead) load(); }, [canRead, load]);

  const open = (nextMode, item = null) => {
    setSelected(item);
    setName(item?.name || '');
    setActive(item ? !!item.active : true);
    setMode(nextMode);
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Indicá el nombre de la herramienta'); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), active };
      if (mode === 'edit') await api.put(`/config/backup-tools/${selected.id}`, payload);
      else await api.post('/config/backup-tools', payload);
      setMode(null); toast.success('Herramienta guardada'); await load();
    } catch (err) { toast.error(errorMessage(err, 'No se pudo guardar la herramienta')); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api.delete(`/config/backup-tools/${deleting.id}`);
      setDeleting(null); toast.success('Herramienta eliminada'); await load();
    } catch (err) { toast.error(errorMessage(err, 'No se pudo eliminar la herramienta')); }
    finally { setSaving(false); }
  };

  if (!canRead) return <Layout><p>No tenés permiso para consultar herramientas de backup.</p></Layout>;

  return <Layout>
    <div className="space-y-6">
      <div className="page-header flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="page-title">Herramientas de Backup</h1><p className="page-subtitle">Catálogo de herramientas utilizadas en las políticas de backup</p></div>
        {canCreate && <Button onClick={() => open('create')}><Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />Nueva herramienta</Button>}
      </div>
      <Card className="card-subtle overflow-hidden"><CardContent className="p-0"><div className="overflow-x-auto">
        <table className="table-subtle">
          <thead><tr><th>NOMBRE</th><th>ESTADO</th><th><div className="text-center">ACCIONES</div></th></tr></thead>
          <tbody>
            {!loading && !error && items.map(item => <tr key={item.id}>
              <td className="font-medium text-primary">{item.name}</td>
              <td><span className={`badge ${item.active ? 'badge-success' : 'badge-gray'}`}>{item.active ? 'Activa' : 'Inactiva'}</span></td>
              <td><div className="flex items-center justify-center gap-0.5">
                <button type="button" className="action-btn" title="Ver" aria-label="Ver herramienta" onClick={() => open('view', item)}><Eye className="h-4 w-4" strokeWidth={1.5} /></button>
                {canUpdate && <button type="button" className="action-btn action-btn-edit" title="Editar" aria-label="Editar herramienta" onClick={() => open('edit', item)}><Edit className="h-4 w-4" strokeWidth={1.5} /></button>}
                {canCreate && <button type="button" className="action-btn action-btn-copy" title="Copiar" aria-label="Copiar herramienta" onClick={() => open('copy', item)}><Copy className="h-4 w-4" strokeWidth={1.5} /></button>}
                {canDelete && <button type="button" className="action-btn action-btn-delete" title="Eliminar" aria-label="Eliminar herramienta" onClick={() => setDeleting(item)}><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>}
              </div></td>
            </tr>)}
            {loading && <tr><td colSpan={3}><div className="empty-state text-sm text-muted-foreground">Cargando herramientas...</div></td></tr>}
            {!loading && error && <tr><td colSpan={3}><div className="empty-state flex flex-col items-center gap-3 text-sm text-destructive" role="alert"><span>{error}</span><Button variant="outline" size="sm" onClick={load}>Reintentar</Button></div></td></tr>}
            {!loading && !error && !items.length && <tr><td colSpan={3}><div className="empty-state"><Database className="h-12 w-12 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} /><p className="empty-state-text">No hay herramientas registradas.</p></div></td></tr>}
          </tbody>
        </table>
      </div></CardContent></Card>
    </div>
    <Dialog open={!!mode} onOpenChange={openState => { if (!openState && !saving) setMode(null); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>{mode === 'view' ? 'Ver herramienta' : mode === 'edit' ? 'Editar herramienta' : mode === 'copy' ? 'Copiar herramienta' : 'Nueva herramienta'}</DialogTitle></DialogHeader>
        <div className="space-y-2 py-2"><Label htmlFor="backup-tool-name">Nombre *</Label><Input id="backup-tool-name" maxLength={100} value={name} disabled={mode === 'view'} onChange={e => setName(e.target.value)} placeholder="Ej.: Veeam" />
          <div className="flex items-center gap-2 pt-2"><Switch id="backup-tool-active" checked={active} disabled={mode === 'view'} onCheckedChange={setActive} /><Label htmlFor="backup-tool-active">Herramienta activa</Label></div>
          {mode === 'edit' && <p className="text-xs text-muted-foreground">Al cambiar el nombre, se actualizará en las políticas que utilizan esta herramienta. Las herramientas inactivas se conservan en las políticas existentes.</p>}
          {mode === 'copy' && <p className="text-xs text-muted-foreground">Ingresá un nombre diferente para la copia.</p>}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setMode(null)} disabled={saving}>Cerrar</Button>{mode !== 'view' && <Button onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>}</DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={!!deleting} onOpenChange={openState => { if (!openState && !saving) setDeleting(null); }}>
      <DialogContent><DialogHeader><DialogTitle>Eliminar herramienta</DialogTitle></DialogHeader><p>¿Eliminar “{deleting?.name}”? No se puede eliminar una herramienta utilizada por una política.</p><DialogFooter><Button variant="outline" onClick={() => setDeleting(null)} disabled={saving}>Cancelar</Button><Button variant="destructive" onClick={remove} disabled={saving}>{saving ? 'Eliminando...' : 'Eliminar'}</Button></DialogFooter></DialogContent>
    </Dialog>
  </Layout>;
};

export default BackupToolsPage;
